import React, { useState, useEffect, memo, useMemo, useRef } from "react";
import DedicatedListButton from "../FormsV2/FormUtils/DedicatedListButton";
import Select from "react-select";
import PropTypes from "prop-types";
import projectConfig from "@projectConfig";
import "./SuperSelect.less";

const customStyles = {
   control: (provided) => ({
      ...provided,
      minHeight: "33px",
      height: "33px",
   }),
   valueContainer: (provided) => ({
      ...provided,
      height: "33px",
      padding: "0 8px",
   }),
   input: (provided) => ({
      ...provided,
      margin: "0px",
   }),
   indicatorsContainer: (provided) => ({
      ...provided,
      height: "33px",
   }),
   option: (provided, state) => ({
      ...provided,
      minHeight: "32px",
      backgroundColor: state.isFocused
         ? projectConfig.cssVariables["--btn-primaryBackground"]
         : "transparent",
      color: state.isFocused ? projectConfig.cssVariables["--nav-background"] : "inherit",
      fontWeight: state.isSelected ? 600 : "normal",
      ":active": {
         ...provided[":active"],
         backgroundColor: projectConfig.cssVariables["--btn-primaryBackground"],
      },
   }),
};

// If you want to use DedicatedListButton you need to provide: seekingOutType, form, listId, getValueFromAdvancedSearch and techname.
const SuperSelect = memo(
   ({
      options = [],
      disabled,
      handleSelect,
      className,
      seekingOutType,
      techname,
      form,
      listId,
      defaultValue,
      placeholder,
      name,
      value: providedValue,
      refreshForm,
      isLoading,
      reloadFieldOptions,
   }) => {
      const [value, setValue] = useState(defaultValue);
      const [dedicatedListTableValue, setdedicatedListTableValue] = useState();

      const userSelectedRef = useRef(false);
      const dedicatedAppliedRef = useRef(false);

      const emptyOptionClass = value?.isPlaceholder ? "empty-option" : "";
      const normalizeVal = (val) => val?.value ?? val;
      const sameValue = (a, b) => String(normalizeVal(a)) === String(normalizeVal(b));

      const findOption = (val) => {
         if (val == null) return undefined;
         const normalized = normalizeVal(val);
         return (
            options.find((option) => String(option?.value) === String(normalized)) ||
            options.find((option) => String(option?.label) === String(val?.label ?? val))
         );
      };

      const setSelectValue = (val) => {
         if (val === null || val === undefined) {
            setValue(null);
            return;
         }
         const next = findOption(val) ?? val;
         setValue((prev) => {
            if (!sameValue(prev, next)) return next;
            const nextIsOption =
               typeof next === "object" && next !== null && next.label !== undefined;
            const prevIsOption =
               typeof prev === "object" && prev !== null && prev.label !== undefined;
            if (nextIsOption && !prevIsOption) return next;
            return prev;
         });
      };

      const getValueFromAdvancedSearch = (val) => {
         const target = Number(val).toFixed(0);
         let selectedOption = options.find((option) => String(option.value) === String(target));

         if (!selectedOption) return;

         const objectWithLowercaseLabel = {
            ...selectedOption,
            lowercaseLabel: (selectedOption.label || "").toLowerCase(),
         };

         dedicatedAppliedRef.current = false;
         setdedicatedListTableValue(objectWithLowercaseLabel);
      };

      const handleChange = (val, actionMeta) => {
         const isUserSelect = actionMeta?.action === "select-option";
         if (!isUserSelect && sameValue(val, value)) return;
         if (isUserSelect) {
            userSelectedRef.current = true;
            dedicatedAppliedRef.current = true;
            if (dedicatedListTableValue) setdedicatedListTableValue(undefined);
         }

         setSelectValue(val);

         handleSelect?.(val, actionMeta);
      };

      const matchingOption = useMemo(() => findOption(value) ?? null, [options, value]);

      useEffect(() => {
         if (!options?.length) return;
         if (dedicatedListTableValue && !dedicatedAppliedRef.current) {
            if (!sameValue(dedicatedListTableValue, value)) {
               handleChange(dedicatedListTableValue);
            }
            dedicatedAppliedRef.current = true;
            setdedicatedListTableValue(undefined);
         }
      }, [options, dedicatedListTableValue, matchingOption, value]);

      useEffect(() => {
         const newValue = form.getFieldValue(techname);
         if (newValue !== value?.value) {
            if (newValue === null || newValue === undefined) {
               setSelectValue(null);
               return;
            }
            setSelectValue(findOption(newValue) ?? newValue);
         } else if (matchingOption && matchingOption !== value) {
            setValue(matchingOption);
         }
      }, [form, techname, options, value?.value]);

      return (
         <>
            <Select
               options={options}
               getOptionLabel={(option) => (option.isDisabled ? null : option.label)}
               className={`ant-select super-select ant-select-enable ${className} ${emptyOptionClass}`}
               isLoading={isLoading}
               classNamePrefix={"super-select"}
               placeholder={placeholder || null}
               components={{
                  IndicatorSeparator: () => null,
                  ClearIndicator: () => null,
               }}
               isDisabled={disabled}
               onChange={handleChange}
               name={name}
               form={form}
               value={providedValue ?? value}
               menuPortalTarget={document.body}
               menuPlacement="auto"
               styles={customStyles}
            />

            {seekingOutType === "3" && listId && form && techname && (
               <DedicatedListButton
                  getValueFromAdvancedSearch={getValueFromAdvancedSearch}
                  disabled={disabled}
                  listId={listId}
                  form={form}
                  fieldTechname={techname}
                  refreshForm={refreshForm}
                  reloadFieldOptions={reloadFieldOptions}
               />
            )}
         </>
      );
   },
);

SuperSelect.propTypes = {
   options: PropTypes.array,
   handleSelect: PropTypes.func,
};

export default SuperSelect;
